import React, { ReactNode } from "react";
import _ from "lodash";
import { FormatMessage } from "react-globalize";
import theme from "styles/theme";
import InfoIcon from "@material-ui/icons/Info";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import { merge } from "glamor";

const memoizeObject = (root: object = {}) => _.memoize((assign: object = {}) => ({ ...root, ...assign }));

interface IProps {
    message: string;
    style?: React.CSSProperties;
    tooltipStyle?: React.CSSProperties;
    classes?: IBadgeClasses;
    variables?: object;
    elements?: object;
    hintIcon?: ReactNode;
    ["data-test"]?: string;
}

interface IBadgeClasses {
    root?: string;
    button?: string;
    icon?: string;
    tooltip?: string;
}

// controlled state for mobile devices
interface IState {
    tooltip: boolean;
}

export default class HintBadge extends React.PureComponent<IProps, IState> {
    private styles = {
        button: {
            backgroundColor: "transparent",
            verticalAlign: "top",
        },
        infoIcon: {
            width: "24px",
            height: "24px",
            color: theme.palette.lighterGrey,
        },
        tooltipText: {
            textAlign: "left",
            fontSize: "14px",
            textIndent: "0px",
            lineHeight: "1.4",
            letterSpacing: "0.2px",
            display: "block",
            maxWidth: "300px",
            paddingButton: "5px",
            wordWrap: "break-word",
            whiteSpace: "pre-wrap",
        },
    };

    render() {
        const {
            message,
            style,
            tooltipStyle,
            variables,
            elements = {},
            classes = {},
            hintIcon,
            ["data-test"]: dataTest,
        } = this.props;
        const { tooltip = false } = this.state || {};
        return (
            <Tooltip
                data-test={dataTest}
                className={classes.tooltip}
                title={
                    <FormatMessage
                        path={message}
                        variables={variables}
                        elements={{ br: <br />, ...elements }}
                        style={this.getTooltipStyles(tooltipStyle)}
                    />
                }
                placement="bottom-start"
                onClose={this.handleTooltip(false)}
                onOpen={this.handleTooltip(true)}
                open={tooltip}
            >
                <IconButton
                    className={classes.button}
                    onClick={this.handleTooltip(true)}
                    style={this.getButtonStyles(style)}
                >
                    {hintIcon ?? <InfoIcon className={classes.icon} style={this.styles.infoIcon} />}
                </IconButton>
            </Tooltip>
        );
    }

    // tslint:disable: member-ordering
    private handleTooltip = _.memoize((tooltip: boolean) => () => this.setState({ tooltip }));
    private getButtonStyles = memoizeObject(this.styles.button);
    private getTooltipStyles = memoizeObject(this.styles.tooltipText);
}

interface IAddBadgeProps {
    hint: string;
    variables?: object;
    elements?: object;
    badgeStyle?: React.CSSProperties;
    classes?: IBadgeClasses;
    hintIcon?: ReactNode;
    ["data-test"]?: string;
}

export class AddHintBadge extends React.PureComponent<IAddBadgeProps, {}> {
    private styles = merge({
        whiteSpace: "nowrap" as "nowrap",
        display: "flex",
        alignItems: "center",
    });

    render() {
        const {
            hint,
            badgeStyle,
            children,
            variables = {},
            elements = {},
            classes = {},
            hintIcon,
            ["data-test"]: dataTest,
        } = this.props;
        return (
            <div {...this.styles} className={classes.root}>
                {children}
                <HintBadge
                    data-test={dataTest}
                    classes={classes}
                    message={hint}
                    variables={variables}
                    elements={elements}
                    style={badgeStyle}
                    hintIcon={hintIcon}
                />
            </div>
        );
    }
}
